import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { firstValueFrom } from 'rxjs';
import { UiConfigBootstrapService } from './core/ui-config-bootstrap.service';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { authInterceptor } from './core/auth/auth.interceptor';
import { errorInterceptor } from './core/error.interceptor';
import { AuthService } from './core/auth/auth.service';
import { SERVICE_STATUS_CHECKS } from './core/status/service-status-check.model';
import { BackofficeStatusCheck } from './core/status/checks/backoffice-status-check';
import { provideNzI18n, it_IT, NZ_DATE_CONFIG } from 'ng-zorro-antd/i18n';
import { provideNzConfig } from 'ng-zorro-antd/core/config';
import { routes } from './app.routes';

import { provideNzIcons } from 'ng-zorro-antd/icon';
import {
  DashboardOutline,
  ProjectOutline,
  ToolOutline,
  DollarOutline,
  DatabaseOutline,
  SettingOutline,
  AppstoreOutline,
  MenuOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  UserOutline,
  TeamOutline,
  SafetyOutline,
  KeyOutline,
  AuditOutline,
  FileTextOutline,
  BarChartOutline,
  FilterOutline,
  PlusOutline,
  DeleteOutline,
  SearchOutline,
  SaveOutline,
  DownloadOutline,
  CheckSquareOutline,
  EditOutline,
  CloseOutline,
  CloseCircleFill,
  ArrowLeftOutline,
  EyeOutline,
  SunOutline,
  MoonOutline,
  CopyOutline,
  LogoutOutline,
  ReloadOutline,
  FileExcelOutline,
  FundOutline,
  UnorderedListOutline,
} from '@ant-design/icons-angular/icons';
import { provideNzDateFnsAdapter } from 'ng-zorro-antd/core/time';


const icons = [
  DashboardOutline,
  ProjectOutline,
  ToolOutline,
  DollarOutline,
  DatabaseOutline,
  SettingOutline,
  AppstoreOutline,
  MenuOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  UserOutline,
  TeamOutline,
  SafetyOutline,
  KeyOutline,
  AuditOutline,
  FileTextOutline,
  BarChartOutline,
  FilterOutline,
  PlusOutline,
  DeleteOutline,
  SearchOutline,
  SaveOutline,
  DownloadOutline,
  CheckSquareOutline,
  EditOutline,
  CloseOutline,
  CloseCircleFill,
  ArrowLeftOutline,
  EyeOutline,
  SunOutline,
  MoonOutline,
  CopyOutline,
  LogoutOutline,
  ReloadOutline,
  FileExcelOutline,
  FundOutline,
  UnorderedListOutline,
];

registerLocaleData(localeIt);

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes),
        provideAnimationsAsync(),
        // errorInterceptor stays last so it can evaluate the response status of whatever
        // authInterceptor forwarded.
        provideHttpClient(withXhr(), withInterceptors([authInterceptor, errorInterceptor])),
        // Interfaccia in italiano (STILE-VISUALE.md §6): date/numeri Angular e testi NG-Zorro.
        { provide: LOCALE_ID, useValue: 'it' },
        provideNzI18n(it_IT),
        { provide: NZ_DATE_CONFIG, useValue: { firstDayOfWeek: 1 } },
        provideNzConfig({
            icon: { nzTheme: 'outline' }
        }),
        provideNzIcons(icons),
        // Register one ServiceStatusCheck provider per service to show on /status — the
        // status page itself never needs to change when a new one is added here.
        { provide: SERVICE_STATUS_CHECKS, useClass: BackofficeStatusCheck, multi: true },
        {
            // Single initializer that runs auth THEN menu/routes sequentially.
            // APP_INITIALIZER with multi:true runs all factories in parallel (Promise.all),
            // so merging into one factory guarantees the token is ready before the API calls.
            // Menu and routes are ui_config rows read with the user's JWT: without a session they would fail with a 401 and leave
            // an error (and an empty menu) waiting for the shell, so they are loaded only when a session was restored; after a
            // login the login page loads them (UiConfigBootstrapService), because a login does not reload the page.
            provide: APP_INITIALIZER,
            useFactory: (auth: AuthService, uiConfig: UiConfigBootstrapService) =>
                async () => {
                    await auth.init();
                    if (auth.isAuthenticated()) {
                        await firstValueFrom(uiConfig.load());
                    }
                },
            deps: [AuthService, UiConfigBootstrapService],
            multi: true,
        }, provideNzDateFnsAdapter(),
    ],
};
